/**
 * Version History Accordion Component
 *
 * Displays complete version history for a recruit's contracts.
 * Shows all versions (active + voided) in reverse chronological order.
 *
 * Features:
 * - Collapsible accordion (collapsed by default)
 * - Version count badge
 * - Timeline visualization
 * - Version details (status, dates, void reason, voided by)
 * - Visual distinction between active and voided
 */

import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import { Loader2, FileText, FileX, CheckCircle2, Clock, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { getContractVersionHistory, type VersionHistoryItem } from '@/services/contractVersionService';
import { formatDistanceToNow } from 'date-fns';
import { parseAsLocalDate } from '@/utils/dateHelpers';
import { ViewContractVersionModal } from './ViewContractVersionModal';

interface VersionHistoryAccordionProps {
  recruitId: string;
  candidateName?: string;
}

// kit badge tints reused across the card
const badgeNeutral: React.CSSProperties = { background: '#EEEADD', color: '#6F6A57' };
const badgeGreen: React.CSSProperties = { background: 'var(--green-700)', color: '#EAE3CF' };
const badgeGold: React.CSSProperties = { background: '#F6EEDA', color: '#7E6426' };
const badgeGreenSoft: React.CSSProperties = { background: '#E2EFE6', color: '#246042' };
const badgeClay: React.CSSProperties = { background: '#F0E7E2', color: '#8A5A47' };

export const VersionHistoryAccordion: React.FC<VersionHistoryAccordionProps> = ({
  recruitId,
  candidateName
}) => {
  const [versions, setVersions] = useState<VersionHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<VersionHistoryItem | null>(null);

  useEffect(() => {
    loadVersionHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when recruitId changes
  }, [recruitId]);

  const loadVersionHistory = async () => {
    setIsLoading(true);
    try {
      const history = await getContractVersionHistory(recruitId);
      setVersions(history);
    } catch (error) {
      console.error('Failed to load version history:', error);
      toast.error('Failed to load version history');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewContract = (version: VersionHistoryItem) => {
    setSelectedVersion(version);
    setViewModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="panel" style={{ padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--lw-muted)', fontSize: 13 }}>
          <Loader2 className="animate-spin" size={16} />
          <span>Loading version history...</span>
        </div>
      </div>
    );
  }

  if (versions.length === 0) {
    return null; // No versions to show
  }

  return (
    <>
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="version-history" style={{ border: '1px solid var(--line)', borderRadius: 'var(--lw-radius)' }}>
          <AccordionTrigger className="px-4 hover:no-underline">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <FileText size={16} style={{ color: 'var(--lw-muted)' }} />
              <span style={{ fontWeight: 600 }}>Version History</span>
              <span className="badge" style={badgeNeutral}>
                {versions.length} version{versions.length !== 1 ? 's' : ''}
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 8 }}>
              {versions.map((version, index) => (
                <VersionCard
                  key={version.id}
                  version={version}
                  isLatest={index === 0}
                  onView={handleViewContract}
                />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* View Contract Modal */}
      <ViewContractVersionModal
        open={viewModalOpen}
        onOpenChange={setViewModalOpen}
        version={selectedVersion}
        candidateName={candidateName}
      />
    </>
  );
};

interface VersionCardProps {
  version: VersionHistoryItem;
  isLatest: boolean;
  onView: (version: VersionHistoryItem) => void;
}

const VersionCard: React.FC<VersionCardProps> = ({ version, isLatest, onView }) => {
  const isActive = version.isActive;
  const isVoided = version.status === 'voided';
  const isFullySigned = version.hasLoSignature && version.hasRecruiterSignature;

  // Determine left-accent + wash based on status
  const accent = isActive
    ? { borderLeft: '4px solid #2E8255', background: '#E9EFE7' }
    : isVoided
      ? { borderLeft: '4px solid #C9C2B0', background: 'var(--cream-2)' }
      : { borderLeft: '4px solid var(--gold)', background: 'var(--gold-wash)' };

  // Status badge tint + label
  const statusStyle = isFullySigned
    ? badgeGreen
    : version.status === 'recruit_signed'
      ? badgeGreenSoft
      : version.status === 'voided'
        ? badgeClay
        : badgeGold;
  const statusLabel = version.status === 'recruit_signed'
    ? 'LO Signed'
    : version.status === 'signed'
      ? 'Fully Signed'
      : version.status.replace('_', ' ').toUpperCase();

  return (
    <div className="panel" style={{ padding: 14, ...accent }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <h4 style={{ fontWeight: 600, margin: 0, fontSize: 14 }}>Version {version.versionNumber}</h4>
              {isActive && <span className="badge" style={badgeGreen}>Active</span>}
              {isVoided && <span className="badge" style={badgeNeutral}>Voided</span>}
              {isLatest && !isVoided && <span className="badge" style={badgeGold}>Latest</span>}
            </div>
            <p style={{ fontSize: 13, color: 'var(--lw-muted)', margin: 0 }}>
              Created {formatDistanceToNow(parseAsLocalDate(version.createdAt), { addSuffix: true })}
            </p>
          </div>

          {/* Actions and Status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button type="button" className="btn-sm" onClick={() => onView(version)}>
              <Eye size={14} />
              View
            </button>

            {/* Status Icon */}
            <div>
              {isVoided ? (
                <FileX size={20} style={{ color: 'var(--lw-muted)' }} />
              ) : isFullySigned ? (
                <CheckCircle2 size={20} style={{ color: '#2E8255' }} />
              ) : (
                <Clock size={20} style={{ color: 'var(--gold-600)' }} />
              )}
            </div>
          </div>
        </div>

        {/* Status Badge */}
        <div>
          <span className="badge" style={statusStyle}>{statusLabel}</span>
        </div>

        {/* Signatures Status */}
        {!isVoided && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {version.hasLoSignature ? (
                <CheckCircle2 size={16} style={{ color: '#2E8255' }} />
              ) : (
                <Clock size={16} style={{ color: 'var(--lw-muted)' }} />
              )}
              <span style={{ color: version.hasLoSignature ? 'var(--green-800)' : 'var(--lw-muted)' }}>LO Signature</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {version.hasRecruiterSignature ? (
                <CheckCircle2 size={16} style={{ color: '#2E8255' }} />
              ) : (
                <Clock size={16} style={{ color: 'var(--lw-muted)' }} />
              )}
              <span style={{ color: version.hasRecruiterSignature ? 'var(--green-800)' : 'var(--lw-muted)' }}>Company Signature</span>
            </div>
          </div>
        )}

        {/* Void Information */}
        {isVoided && version.voidedAt && (
          <div style={{ borderTop: '1px solid var(--line-soft)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 13 }}>
              <p style={{ fontWeight: 600, color: 'var(--ink-soft)', margin: 0 }}>Void Reason:</p>
              <p style={{ color: 'var(--lw-muted)', marginTop: 4, marginBottom: 0, fontStyle: 'italic' }}>"{version.voidReason}"</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--lw-muted)' }}>
              <span>Voided by {version.voidedByUser?.full_name || 'Unknown'}</span>
              <span>{formatDistanceToNow(parseAsLocalDate(version.voidedAt), { addSuffix: true })}</span>
            </div>
          </div>
        )}

        {/* Signature Dates */}
        {!isVoided && (version.loanOfficerSignatureDate || version.recruiterSignatureDate) && (
          <div style={{ borderTop: '1px solid var(--line-soft)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11.5, color: 'var(--lw-muted)' }}>
            {version.loanOfficerSignatureDate && (
              <p style={{ margin: 0 }}>
                <strong>LO Signed:</strong> {parseAsLocalDate(version.loanOfficerSignatureDate).toLocaleString()}
              </p>
            )}
            {version.recruiterSignatureDate && (
              <p style={{ margin: 0 }}>
                <strong>Company Signed:</strong> {parseAsLocalDate(version.recruiterSignatureDate).toLocaleString()}
              </p>
            )}
          </div>
        )}

        {/* Parent Version Link */}
        {version.parentInstanceId && (
          <div style={{ fontSize: 11.5, color: 'var(--lw-muted)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span>↳</span>
              <span>Created from previous version</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
